"use client";

import type { ProductDetails } from "@/lib/centra";
import { addLeadingSlash } from "@frend-digital/utils";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./Reviews.module.css";

export const AiSummary = ({ product, slug }: { product: ProductDetails; slug: string[] }) => {
	const [revealed, setRevealed] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset detection per product, same as RatingStars
	useEffect(() => {
		setRevealed(false);

		const el = containerRef.current;
		if (!el) return;

		const checkContent = () => {
			if (el.textContent && el.textContent.trim().length > 0) {
				setRevealed(true);
				observer.disconnect();
			}
		};

		const observer = new MutationObserver(checkContent);
		observer.observe(el, {
			childList: true,
			characterData: true,
			subtree: true,
		});

		checkContent();

		return () => {
			observer.disconnect();
		};
	}, [product.sku]);

	// Lipscore owns the "Based on N votes" markup, so delegate the click from the container.
	const scrollToReviews = (event: React.MouseEvent) => {
		if (!(event.target as HTMLElement).closest(".lipscore-ai-summary-based-on")) return;

		const id = "lipscore-review-list";
		const details = document.getElementById(id)?.closest("details");
		if (!details) return;

		details.open = true;
		setTimeout(() => {
			document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 32);
	};

	return (
		<div className={revealed ? undefined : styles.aiSummaryCollapsed}>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: delegated click on Lipscore-rendered link text */}
			<div
				ref={containerRef}
				onClick={scrollToReviews}
				className="lipscore-ai-summary vikingLipscoreAiSummary"
				data-ls-product-name={product.name}
				data-ls-brand={product.brand || "Viking"}
				data-ls-product-id={product.centraProductNumber}
				data-ls-product-url={`https://vikingfootwear.com${addLeadingSlash(slug.join("/"))}`}
				data-ls-variant-id={product.displayItemId}
				data-ls-variant-name={product.variantName}
				data-ls-sku={product.sku}
				data-ls-description={product.shortDescription}
				data-ls-image-url={product.media.at(0)?.href}
				data-ls-price={product.priceAsNumber}
				data-ls-price-currency={product.price?.match(/[A-Z]{3}$/)?.[0]}
				data-ls-category={product.canonicalCategoryUri}
			/>
		</div>
	);
};
